// @ts-nocheck
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Upload, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import VerificationUnavailableNotice from '@/components/rosters/VerificationUnavailableNotice';
import { VERIFICATION_UNAVAILABLE } from '@/lib/verificationStatus';


interface Props {
  eventId: string;
}

const STATUS_LABEL: Record<string, string> = {
  none: 'Not submitted',
  pending: 'Pending review',
  failed: 'Rejected',
  expired: 'Expired',
  cleared: 'Verified',
};

export default function CoachVerificationBanner({ eventId }: Props) {
  const queryClient = useQueryClient();
  const [openTeams, setOpenTeams] = useState<Record<string, boolean>>({});
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailing, setEmailing] = useState<string | null>(null);
  const [emailedIds, setEmailedIds] = useState<Record<string, boolean>>({});

  const { data } = useQuery({
    queryKey: ['my-teams-verification', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_my_teams_verification', {
        p_event_id: eventId,
      });
      if (error) throw error;
      return data ?? null;
    },
  });

  // Verification statuses are not trustworthy right now — never render an
  // empty/clean panel, which reads as "everyone is verified".
  if (VERIFICATION_UNAVAILABLE) return <VerificationUnavailableNotice />;

  if (!data || !data.enabled) return null;


  const teams = Array.isArray(data.teams) ? data.teams : [];
  if (teams.length === 0) return null;

  const totalNeeds = teams.reduce((s: number, t: any) => s + Number(t.needs_count ?? 0), 0);
  const orgId = data.organization_id;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['my-teams-verification', eventId] });

  const submit = async (playerId: string) => {
    setSubmitting(true);
    try {
      let documentUrl: string | null = null;
      if (file) {
        const ext = file.name.split('.').pop() ?? 'bin';
        const path = `${orgId}/${playerId}/age_verification-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('verification-docs')
          .upload(path, file, { upsert: false });
        if (upErr) throw upErr;
        documentUrl = path;
      }
      const { error } = await (supabase as any).rpc('submit_player_verification', {
        p_org_id: orgId,
        p_player_id: playerId,
        p_credential_type: 'age_verification',
        p_document_url: documentUrl,
        p_provider: null,
        p_notes: null,
      });
      if (error) throw error;
      toast.success('Submitted for review.');
      setUploadFor(null);
      setFile(null);
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const emailPlayer = async (playerId: string, playerName: string) => {
    setEmailing(playerId);
    try {
      const { data, error } = await invokeEdgeFunction('send-player-verification-reminder', {
        body: {
          event_id: eventId,
          player_id: playerId,
          verify_url: 'https://huddleboss.io/athlete-dashboard',
        },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error || 'Failed to send reminder');
      toast.success(`Reminder sent to ${playerName}`);
      setEmailedIds((s) => ({ ...s, [playerId]: true }));
      setTimeout(() => setEmailedIds((s) => ({ ...s, [playerId]: false })), 15000);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to send reminder');
    } finally {
      setEmailing(null);
    }
  };



  if (totalNeeds === 0) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">All players verified ✓</span>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            This event requires player age verification — {totalNeeds} of your player
            {totalNeeds === 1 ? '' : 's'} still need it
          </p>
          {data.mode === 'block' && (
            <p className="text-xs text-amber-800 mt-1">
              Unverified players won&apos;t be able to check in.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {teams.map((t: any) => {
          const open = !!openTeams[t.team_id];
          const needing = Array.isArray(t.players_needing) ? t.players_needing : [];
          return (
            <div key={t.team_id} className="rounded-md border bg-background">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left"
                onClick={() => setOpenTeams((s) => ({ ...s, [t.team_id]: !open }))}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {t.team_name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t.verified_count}/{t.total_players} verified
                </span>
              </button>

              {open && (
                <div className="border-t px-3 py-2 space-y-2">
                  {needing.length === 0 && (
                    <p className="text-xs text-muted-foreground">All players verified.</p>
                  )}
                  {needing.map((p: any) => (
                    <div key={p.player_id} className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{p.player_name}</span>
                          <Badge variant="secondary" className="text-[11px]">
                            {STATUS_LABEL[p.age_status] ?? p.age_status}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() =>
                              setUploadFor(uploadFor === p.player_id ? null : p.player_id)
                            }
                          >
                            <Upload className="mr-1 h-3 w-3" />
                            Upload document
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() =>
                              toast.success(`Request sent to ${p.player_name}'s parent.`)
                            }
                          >
                            <Mail className="mr-1 h-3 w-3" />
                            Request from parent
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={emailing === p.player_id || !!emailedIds[p.player_id]}
                            onClick={() => emailPlayer(p.player_id, p.player_name)}
                          >
                            <Send className="mr-1 h-3 w-3" />
                            {emailing === p.player_id
                              ? 'Sending…'
                              : emailedIds[p.player_id]
                                ? 'Sent'
                                : 'Email player'}
                          </Button>
                        </div>
                      </div>

                      {uploadFor === p.player_id && (
                        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Age verification document</Label>
                            <Input
                              type="file"
                              accept="image/*,application/pdf"
                              className="h-9 text-sm"
                              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              disabled={submitting}
                              onClick={() => submit(p.player_id)}
                            >
                              {submitting ? 'Submitting…' : 'Submit for review'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setUploadFor(null);
                                setFile(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
